import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { siteConfig } from "../../config/siteConfig";

const ProfileModal = ({ isOpen, onClose }) => {
  const { user, profile, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    profession: siteConfig.niche.aspirantLabel,
    bio: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const dialogRef = useRef(null);

  // Cargar datos del perfil
  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        name: profile?.name || user?.user_metadata?.name || "",
        email: user?.email || "",
        phone: profile?.phone || user?.user_metadata?.phone || "",
        profession: profile?.profession || siteConfig.niche.aspirantLabel,
        bio: profile?.bio || "",
      });
    }
  }, [user, profile, isOpen]);

  // Escape para cerrar + trampa de foco (Tab/Shift+Tab) + foco inicial y restauración
  useEffect(() => {
    if (!isOpen) return;

    // Recordar el elemento enfocado antes de abrir el modal para restaurarlo al cerrar
    const previouslyFocused =
      typeof document !== "undefined" ? document.activeElement : null;

    const getFocusable = () => {
      const node = dialogRef.current;
      if (!node) return [];
      return Array.from(
        node.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
    };

    // Foco inicial: el primer elemento enfocable dentro del diálogo (o el diálogo mismo)
    const focusFirst = () => {
      const focusable = getFocusable();
      if (focusable.length > 0) {
        focusable[0].focus();
      } else if (dialogRef.current) {
        dialogRef.current.focus();
      }
    };
    focusFirst();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      const focusable = getFocusable();
      if (focusable.length === 0) {
        e.preventDefault();
        if (dialogRef.current) dialogRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || !dialogRef.current?.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !dialogRef.current?.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      // Restaurar el foco al elemento que lo tenía antes de abrir el modal
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: "", text: "" });

    const result = await updateProfile({
      name: formData.name,
      phone: formData.phone,
      profession: formData.profession,
      bio: formData.bio,
    });

    if (result.success) {
      setMessage({
        type: "success",
        text: "Profiili päivitetty onnistuneesti",
      });
      setTimeout(() => {
        setIsEditing(false);
        setMessage({ type: "", text: "" });
      }, 1500);
    } else {
      setMessage({ type: "error", text: result.error });
    }
    setIsLoading(false);
  };

  const handleEditClick = (e) => {
    e.preventDefault();
    setIsEditing(true);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    setFormData({
      name: profile?.name || user?.user_metadata?.name || "",
      email: user?.email || "",
      phone: profile?.phone || user?.user_metadata?.phone || "",
      profession: profile?.profession || siteConfig.niche.aspirantLabel,
      bio: profile?.bio || "",
    });
    setIsEditing(false);
    setMessage({ type: "", text: "" });
  };

  // Input premium : 1px border, anneau de focus teal soft (jamais focus:ring-0)
  const inputBase =
    "w-full px-4 py-3 rounded-xl text-sm transition-colors duration-200 focus:outline-none";
  const inputEditable =
    "bg-white border border-slate-300 text-slate-800 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20";
  const inputLocked =
    "bg-slate-50 border border-slate-200 text-slate-700 cursor-not-allowed";
  const inputClass = (editable) =>
    `${inputBase} ${editable ? inputEditable : inputLocked}`;

  return (
    <div className="fixed inset-0 z-[200] overflow-y-auto">
      <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-md" />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-modal-title"
          tabIndex={-1}
          className="relative w-full max-w-2xl transform max-h-[90vh] overflow-y-auto rounded-2xl bg-white elev-3 ring-1 ring-slate-900/5 animate-slideUp focus:outline-none"
        >
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-teal-700 to-cyan-600" />

          <div className="px-8 pt-10 pb-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-700 to-cyan-600 flex items-center justify-center text-white text-2xl font-bold elev-2">
                  {profile?.name?.charAt(0) || user?.email?.charAt(0) || "U"}
                </div>
                <div>
                  <h2
                    id="profile-modal-title"
                    className="text-2xl font-black text-slate-800"
                  >
                    Profiilini
                  </h2>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Cerrar"
                className="p-2 rounded-xl text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-600/40"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Mensaje — éxito = emerald (intacto) · error = red/rose (semántica correcta) */}
            {message.text && (
              <div
                role={message.type === "success" ? "status" : "alert"}
                aria-live="polite"
                className={`mb-6 p-4 rounded-xl flex items-start gap-3 ${
                  message.type === "success"
                    ? "bg-emerald-50 border border-emerald-200"
                    : "bg-rose-50 border border-rose-200"
                }`}
              >
                <span
                  className={`text-sm ${
                    message.type === "success"
                      ? "text-emerald-700"
                      : "text-rose-700"
                  }`}
                >
                  {message.text}
                </span>
              </div>
            )}

            {/* Formulario */}
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="profile-name"
                      className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"
                    >
                      Koko nimi
                    </label>
                    <input
                      id="profile-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={inputClass(isEditing)}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="profile-email"
                      className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"
                    >
                      Sähköpostiosoite
                    </label>
                    <input
                      id="profile-email"
                      type="email"
                      value={formData.email}
                      disabled
                      className={inputClass(false)}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="profile-phone"
                      className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"
                    >
                      Puhelin
                    </label>
                    <input
                      id="profile-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={!isEditing}
                      placeholder={siteConfig.niche.phonePlaceholder}
                      className={inputClass(isEditing)}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="profile-profession"
                      className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"
                    >
                      Ammatti
                    </label>
                    <input
                      id="profile-profession"
                      type="text"
                      name="profession"
                      value={formData.profession}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={inputClass(isEditing)}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="profile-bio"
                    className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"
                  >
                    Esittely
                  </label>
                  <textarea
                    id="profile-bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    disabled={!isEditing}
                    rows="4"
                    placeholder="Kerro hieman itsestäsi..."
                    className={inputClass(isEditing)}
                  />
                </div>
              </div>

              {/* Botones */}
              <div className="mt-8 flex items-center justify-end gap-4">
                {!isEditing ? (
                  <>
                    <button
                      type="button"
                      onClick={onClose}
                      className="btn-ghost focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-600/40"
                    >
                      Sulje
                    </button>
                    <button
                      type="button"
                      onClick={handleEditClick}
                      className="btn-primary glow-cta focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-600/40"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Muokkaa profiilia
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="btn-ghost focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-600/40"
                    >
                      Peruuta
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="btn-primary glow-cta focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-600/40 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? "Tallennetaan..." : "Tallenna muutokset"}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
